import React, { useEffect, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { TAB_BAR_HEIGHT } from 'shared/components/TabBar/TabBar.constants';
import Button from 'shared/components/vxl/Button/Button';
import Close from 'shared/components/vxl/icon/icons/Close';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import { isWeb } from 'shared/constants/platform';
import useOnMainPage from 'shared/hooks/useOnMainPage';
import theme from 'shared/styles/theme';

interface SnackbarProps {
  /** Message to be displayed on the snackbar. */
  text: string;
  /** Text to be displayed on the snackbar button. */
  buttonLabel?: string;
  /** Action to be performed when button is clicked. */
  buttonOnPress?: PressAction;
  /** Amount of time in millisecond the snackbar will remain visible,
   * default is 2750 ms, which is correspond to Android LONG_DURATION.
   */
  duration?: number;
  /** Disable auto hide, snackbar will remain visible until it's manually dismiss. */
  disableAutoHide?: boolean;
  /** Callback function for post auto-hide handling.*/
  onHide?: () => void;
}

type PressAction = ((...args: unknown[]) => void) | 'CLOSE';

export const SNACKBAR_TIMEOUT = 2750; // Android LONG_DURATION default
const SNACKBAR_MAX_WIDTH = 900;

export const Snackbar = ({
  text,
  buttonLabel,
  buttonOnPress,
  onHide,
  duration = SNACKBAR_TIMEOUT,
  disableAutoHide = false,
}: SnackbarProps) => {
  const [isVisible, setIsVisible] = useState(true);
  const { isOnMainPage } = useOnMainPage();
  const { bottom: bottomInset } = useSafeAreaInsets();

  useEffect(() => {
    if (isVisible) {
      const timeout = setTimeout(() => {
        if (!disableAutoHide) {
          setIsVisible(false);
          onHide?.();
        }
      }, duration);
      return () => clearTimeout(timeout);
    }
  }, [isVisible, duration, disableAutoHide, onHide]);

  function executeAction(handlePress: PressAction) {
    if (typeof handlePress === 'function') {
      handlePress();
      setIsVisible(false);
    } else if (handlePress === 'CLOSE') {
      setIsVisible(false);
    }
  }

  if (!isVisible) {
    return null;
  }

  const webPositionStyle = isOnMainPage
    ? styles.snackbarOnMainPage
    : styles.snackbarNotOnMainPage;
  const nativeBottom = isOnMainPage
    ? bottomInset + TAB_BAR_HEIGHT + theme.spacing.s
    : bottomInset;

  return (
    <View
      style={[
        styles.container,
        isWeb ? webPositionStyle : { bottom: nativeBottom },
      ]}
    >
      <BodyBaseText
        style={styles.labelText}
        numberOfLines={0}
        ellipsizeMode="tail"
      >
        {text}
      </BodyBaseText>

      {buttonOnPress && buttonLabel ? (
        <View style={styles.buttonStyle}>
          <Button
            label={buttonLabel}
            onDark
            onPress={() => executeAction(buttonOnPress)}
            size="SMALL"
            type="SECONDARY"
            width="HUG"
          />
        </View>
      ) : null}
      {buttonOnPress && !buttonLabel ? (
        // TODO JT-110771 extract into a component
        <Pressable onPress={() => executeAction(buttonOnPress)}>
          <Close
            iconAccessibilityLabel="CLOSE"
            iconColor={theme.color.text.inverse}
            iconSize={theme.spacing.l}
            iconKey="CLOSE"
          />
        </Pressable>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  buttonStyle: {
    marginVertical: -16, // TODO https://varomoney.atlassian.net/browse/JT-110737
    minWidth: 0,
    overflow: 'hidden',
  },
  container: {
    alignItems: 'center',
    backgroundColor: theme.color.status.info,
    borderRadius: 4,
    flexBasis: 100,
    flexDirection: 'row',
    justifyContent: 'space-between',
    left: theme.spacing.m,
    maxWidth: SNACKBAR_MAX_WIDTH,
    minHeight: 54,
    overflow: 'hidden',
    padding: theme.spacing.m,
    position: 'absolute',
    right: theme.spacing.m,
    zIndex: 1,
  },
  labelText: {
    color: theme.color.text.inverse,
    flex: 1,
    marginEnd: 8,
    minWidth: 0,
    overflow: 'hidden',
  },
  snackbarNotOnMainPage: {
    bottom: theme.spacing.xxl,
  },
  snackbarOnMainPage: {
    bottom: theme.spacing.xxl + TAB_BAR_HEIGHT,
  },
});

export default Snackbar;
